import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PublicHeader from "@/components/layout/PublicHeader";
import PremiumWorkerForm from "@/components/PremiumWorkerForm";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/useSEO";
import { Sparkles, Clock, ShieldCheck, Phone, Brain } from "lucide-react";

const PERKS = [
  { icon: Sparkles, t: "Discoverable by 700+ employers paying $700/mo",   d: "You appear on the Premium tab — the most-clicked area of the Hire dashboard." },
  { icon: Clock,    t: "24-hour staff review",                            d: "Submit once. Within 24 h you'll get an email — approved, rejected, or a request for a quick call." },
  { icon: Phone,    t: "We may call to verify",                           d: "If your CV looks strong, expect a 10-min call to validate. We protect the Premium brand." },
  { icon: ShieldCheck, t: "Privacy-first contact",                        d: "Employers never see your raw email. All outreach is routed through Maqam's proxy." },
];

export default function BecomePremium() {
  useSEO({
    title: "Become a Premium Worker — Maqam",
    description: "Get listed on Maqam's curated Premium list. 24-hour review, then visible to every paying employer on the platform.",
    path: "/become-premium",
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [iqStatus, setIqStatus] = useState({ checked: false, has_result: false });

  // Check if the user has a fresh (≤30d) IQ test on record
  React.useEffect(() => {
    if (!user) return;
    api.get("/premium/iq-test/latest")
      .then((r) => setIqStatus({ checked: true, ...r.data }))
      .catch(() => setIqStatus({ checked: true, has_result: false }));
  }, [user]);

  const handleSubmit = async (payload) => {
    if (!user) {
      toast.error("Please log in or create an account first.");
      navigate("/login?next=/become-premium");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/premium/self-apply", payload);
      toast.success(`Submitted! IQ ${r.data.iq_score}. We'll review within 24 hours.`);
      navigate(`/become-premium/submitted?id=${r.data.submission_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "iq_test_required") {
        toast.error(detail.message || "Please complete the Maqam IQ test first.");
        navigate(detail.cta || "/become-premium/iq-test");
      } else if (detail?.code === "already_submitted") {
        toast.error(detail.message || "You already have an application under review.");
      } else {
        toast.error("Could not submit. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0c0d] text-white" data-testid="become-premium-page">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">/ JOIN THE PREMIUM LIST</div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.9]">
            Get hand-picked.
            <br />
            <span className="italic text-white/60">Get hired.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
            Premium Workers appear on a curated list that paying employers ($700/mo)
            see first. Apply once, get reviewed within 24&nbsp;hours, and skip the
            résumé pile forever.
          </p>
        </div>
      </section>

      {/* Perks strip */}
      <section className="border-b border-white/10 bg-[#141416]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {PERKS.map((p) => (
            <div key={p.t}>
              <p.icon className="w-6 h-6 text-white/80 mb-3" strokeWidth={1.5} />
              <div className="font-display font-bold text-sm mb-1.5">{p.t}</div>
              <div className="text-xs text-white/55 leading-relaxed">{p.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section>
        <div className="max-w-2xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {/* IQ-test gate banner */}
          {iqStatus.checked && !iqStatus.has_result ? (
            <div className="mb-10 border-2 border-[#8b5cf6] bg-[#8b5cf6]/[0.05] p-6" data-testid="iq-gate-banner">
              <div className="flex items-start gap-4">
                <Brain className="w-8 h-8 text-[#8b5cf6] flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#8b5cf6] mb-1">/ STEP 1 OF 2 · REQUIRED</div>
                  <div className="font-display font-bold text-xl mb-2">Take the Maqam IQ assessment first</div>
                  <p className="text-sm text-white/70 mb-4">
                    Every Premium applicant takes a 15-minute Mensa-style
                    cognitive test. Once done, return here to fill in your
                    profile. Result stays valid for 30 days.
                  </p>
                  <button
                    onClick={() => navigate("/become-premium/iq-test")}
                    className="bg-[#8b5cf6] text-white hover:bg-[#8b5cf6]/85 font-bold px-6 py-3 inline-flex items-center gap-2 transition-colors"
                    data-testid="iq-take-test-btn"
                  >
                    Take the IQ test now →
                  </button>
                </div>
              </div>
            </div>
          ) : iqStatus.has_result ? (
            <div className="mb-10 border border-[#34C759]/40 bg-[#34C759]/[0.05] p-5 flex items-center gap-3" data-testid="iq-passed-banner">
              <Brain className="w-6 h-6 text-[#34C759] flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <div className="font-display font-bold">
                  IQ assessment complete · score <span className="text-[#34C759]">{iqStatus.iq}</span>
                </div>
                <div className="text-xs text-white/55 mt-0.5">
                  {iqStatus.raw}/{iqStatus.total} correct · {iqStatus.percentile}<sup>th</sup> percentile
                </div>
              </div>
            </div>
          ) : null}

          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-4">/ STEP 2 · APPLICATION</div>
          <h2 className="font-display font-bold text-3xl mb-2">Tell us about yourself</h2>
          <p className="text-sm text-white/55 mb-10">
            All fields are reviewed manually. The more detail you give, the faster you're approved.
          </p>
          <PremiumWorkerForm
            mode="self"
            onSubmit={handleSubmit}
            submitting={submitting}
            initial={user ? { full_name: user.full_name, email: user.email } : {}}
          />
        </div>
      </section>
    </div>
  );
}
